import React from "react"
import { Text } from "components/text/Text"
import styled from "styled-components"
import { useBreakpoints } from "src/lib/hooks/use-breakpoints"
import { colors } from "src/styles/colors"
import markdownToReact from "src/lib/utils/markdown"
import QUOTE from "assets/icons/quote.svg"
import PERSON from "assets/icons/person.svg"
import DATE from "assets/icons/date.svg"
import ImgWrapper from "components/image/ImageWrapper"
import { Heading } from "components/heading/Heading"

type Props = {
  author: any
  content: any
  createdAt: string
  seo: any
  subtitle: any
  title: string
  titleImage: any
}

const Markdown = styled.div<any>`
  width: 100%;
  overflow: hidden;
  font-size: 16px;
  color: ${colors.black};
  & h1,
  h2 {
    margin-top: 50px;
    margin-bottom: 25px;
    font-size: ${({ minTablet }) => (minTablet ? "26px" : "22px")};
    color: ${colors.blueGray};
    font-weight: 600;
  }
  & h3,
  h4,
  h5 {
    margin-top: 35px;
    margin-bottom: 20px;
    font-size: ${({ minTablet }) => (minTablet ? "26px" : "22px")};
    color: ${colors.blueGray};
    font-weight: 600;
  }
  & p {
    line-height: 25px;
    margin-top: 0px;
    margin-bottom: 16px;
  }
  & strong {
    font-weight: 600;
  }
  & a {
    color: ${colors.primary};
    text-decoration: none;
  }
  & ul,
  ol,
  dl {
    padding-left: 30px;
    li {
      margin-bottom: 4px;
      padding-left: 4px;
    }
  }
  & iframe {
    height: ${({ minTablet }) => (minTablet ? "420px" : "220px")};
  }
  & blockquote {
    position: relative;
    color: ${colors.quote};
    font-size: 18px;
    margin-left: ${({ minTablet }) => (minTablet ? "49px" : "37px")};
    margin-bottom: ${({ minTablet }) => (minTablet ? "50px" : "24px")};
    margin-right: 0;
    & p {
      line-height: 28px;
    }
    &:after {
      position: absolute;
      content: "";
      background-image: url(${QUOTE});
      background-size: ${({ minTablet }) =>
        minTablet ? "33px 26px" : "28px 21px"};
      width: ${({ minTablet }) => (minTablet ? "33px" : "28px")};
      height: ${({ minTablet }) => (minTablet ? "26px" : "21px")};
      left: ${({ minTablet }) => (minTablet ? "-49px" : "-37px")};
      top: 4px;
    }
  }
  & img {
    margin: ${({ minTablet }) =>
      minTablet ? "56px auto 40px auto" : "24px auto 24px auto"};
    max-width: 100%;
    display: block;
    height: auto;
    box-sizing: border-box;
    border: 1px solid ${colors.whiteBorder};
    border-radius: 10px;
    background-color: ${colors.white};
  }
  iframe {
    margin: ${({ minTablet }) =>
      minTablet ? "56px 0 40px 0" : "24px 0 24px 0"};
    width: 100%;
    box-sizing: border-box;
    border: 1px solid ${colors.whiteBorder};
    border-radius: 10px;
    background-color: ${colors.white};
  }
`
const SubTitle = styled.div<any>`
  color: ${colors.black};
  text-align: left;
  font-size: 18px;
  line-height: 28px;
  margin: ${({ minTablet }) => (minTablet ? "47px 0 30px 0" : "21px 0 24px 0")};
  font-weight: 600;
  & p {
    margin: 0;
  }
`
const IconsWrapper = styled.div<any>`
  display: flex;
  justify-content: center;
  margin-top: ${({ minTablet }) => (minTablet ? "10px" : "20px")};
  & span {
    display: flex !important;
    align-items: center;
  }
  & img {
    margin-right: 5px;
  }
`
const Author = styled.div<any>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ minTablet }) => (minTablet ? "row" : "column")};
  padding: ${({ minTablet }) => (minTablet ? "32px 72px" : "21px 16px")};
  border-radius: 10px;
  margin: ${({ minTablet }) => (minTablet ? "40px 0 80px 0" : "20px 0 40px 0")};
  background-color: ${colors.lightTeal};
  & span {
    display: block;
    line-height: 22px;
  }
  & .text {
    display: flex;
    flex-direction: column;
  }
`
const AuthorMini = styled.div<any>`
  position: absolute;
  width: 168px;
  left: calc(100% + 24px);
  top: 160px;
  & span {
    display: block;
    line-height: 22px;
    opacity: 0.85;
  }
`
const Wrapper = styled.div<any>`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  width: ${({ minTablet }) => (minTablet ? "744px" : "100%")};
  margin: 0 auto;
  margin-top: ${({ minTablet }) => (minTablet ? "48px" : "30px")};
`

export const ArticleContent: React.FC<Props> = ({
  content: { content },
  author: {
    name,
    description: { description },
    image,
  },
  createdAt,
  subtitle: { subtitle },
  title,
  titleImage,
}) => {
  const { minTablet } = useBreakpoints()
  return (
    <Wrapper minTablet={minTablet}>
      {minTablet && (
        <AuthorMini>
          <ImgWrapper
            img={image}
            width={72}
            style={{ borderRadius: "50%", marginBottom: 20 }}
          />
          <Text size={14} fontWeight={700} style={{ marginBottom: 8 }}>
            {name}
          </Text>
          <Text size={14}>{description}</Text>
        </AuthorMini>
      )}
      <Heading
        color={colors.blueGray}
        textAlign="center"
        size={minTablet ? 30 : 26}
        fontWeight={700}
      >
        {title}
      </Heading>
      <IconsWrapper minTablet={minTablet}>
        <Text color={colors.gray11} textAlign="center" size={14}>
          <img src={PERSON} alt="person" loading="lazy" />
          {name}
        </Text>
        <Text
          color={colors.gray11}
          textAlign="center"
          size={14}
          style={{ marginLeft: 23 }}
        >
          <img src={DATE} alt="date" loading="lazy" />
          {createdAt}
        </Text>
      </IconsWrapper>
      <SubTitle minTablet={minTablet}>{markdownToReact(subtitle)}</SubTitle>
      <Markdown minTablet={minTablet}>{markdownToReact(content)}</Markdown>
      <Author minTablet={minTablet}>
        <ImgWrapper
          img={image}
          width={minTablet ? 160 : 80}
          style={{
            borderRadius: "50%",
            marginRight: minTablet ? 56 : 0,
            marginBottom: minTablet ? 0 : 11,
            minWidth: minTablet ? 160 : 80,
          }}
        />
        <div className="text">
          <Text
            size={20}
            color={colors.gray3}
            textAlign={minTablet ? "left" : "center"}
            fontWeight={600}
            style={{ marginBottom: 8 }}
          >
            {name}
          </Text>
          <Text textAlign={minTablet ? "left" : "center"} size={14}>
            {description}
          </Text>
        </div>
      </Author>
    </Wrapper>
  )
}
